// ═══════════════════════════════════════════════════════════════
// matchVendors.js — verticalstack client-side vendor recommendation
// Path: src/components/matchVendors.js
//
// S8.F (June 29, 2026) — NEW FILE. Pure ranking module (same family as
// trustack.js). Ranks the platform-wide /vendors roster against a job by
// trade overlap + service area + Trustack score. No Firestore, no network
// — input is a work-order-like object + the vendor array, output is a
// ranked list.
//
// WHY CLIENT-SIDE (AS-S8-MATCHSUBS-SCHEMA-STALE — rewrite at S9)
//   The deployed matchSubcontractors CF (AI-06) is the LLM dispatch model,
//   but its query (vendors.where('clientId').where('verified'==true)) and
//   field reads (serviceCategories / averageRating / jobsCompleted /
//   serviceAreas / lastActiveAt) are ALL old-epoch — none exist on the
//   S8.A §6 schema (vendors are platform-wide, tier-based, trades[]). So
//   against the current roster the CF returns {matches:[]} silently. Its
//   real home is job-attached dispatch in Marketplace (S9), where it gets
//   rewritten to the §6 schema + redeployed. Until then this client-side
//   matcher proves the recommendation UX against real seeded data with
//   zero CF redeploy / API spend. At S9 it becomes the offline fallback.
//
// SCORING (composite, weights renormalize when area is omitted)
//   trade overlap 60 — does the vendor's trades[] match the job category?
//   service area  25 — same city / same state / nearby?
//   Trustack      15 — computeTrustack(vendor).score (dormant → 0 today)
//   Vendors with zero trade overlap are filtered out (don't suggest a
//   lawyer for a plumbing job).
// ═══════════════════════════════════════════════════════════════

import { computeTrustack } from './trustack';

const WEIGHTS = { trade: 60, area: 25, trust: 15 };

function norm(s) {
  return String(s || '').toLowerCase().replace(/[_\s]+/g, ' ').trim();
}

// Trade overlap: 100 exact, 80 substring, scaled token overlap, else 0.
function tradeOverlapScore(jobCategory, vendor) {
  const job = norm(jobCategory);
  if (!job) return 0;
  const jobTokens = job.split(' ').filter(Boolean);
  const trades = (vendor.trades || []).map(norm);
  let best = 0;
  for (const t of trades) {
    if (t === job) return 100;
    if (t.includes(job) || job.includes(t)) { best = Math.max(best, 80); continue; }
    const tTokens = t.split(' ');
    const overlap = jobTokens.filter((jt) => tTokens.includes(jt)).length;
    if (overlap > 0) best = Math.max(best, Math.round(60 * (overlap / jobTokens.length)));
  }
  return best;
}

// Area: 100 same, 70 same state (last comma token), 60 substring, 0 else.
// Returns null when no serviceArea is supplied (neutral — weight redistributes).
function areaScore(serviceArea, vendor) {
  const a = norm(serviceArea);
  if (!a) return null;
  const va = norm(vendor.area);
  if (!va) return 0;
  if (va === a) return 100;
  const aState = a.split(',').pop().trim();
  const vState = va.split(',').pop().trim();
  if (aState && aState === vState) return 70;
  if (va.includes(a) || a.includes(va)) return 60;
  return 0;
}

// matchVendors(workOrder, vendors, opts) →
//   [ { vendor, score, trade, area, trust, reasons[] }, ... ]  (ranked, top N)
export function matchVendors(workOrder, vendors, opts = {}) {
  const topN = opts.topN || 3;
  const { jobCategory, serviceArea } = workOrder || {};
  if (!Array.isArray(vendors)) return [];

  const scored = vendors.map((v) => {
    const trade = tradeOverlapScore(jobCategory, v);
    const area = areaScore(serviceArea, v);
    const trust = computeTrustack(v).score; // 0..100 | null
    const trustVal = trust == null ? 0 : trust;

    let composite;
    if (area == null) {
      // No area supplied — renormalize over trade + trust only.
      const denom = WEIGHTS.trade + WEIGHTS.trust;
      composite = Math.round((trade * WEIGHTS.trade + trustVal * WEIGHTS.trust) / denom);
    } else {
      composite = Math.round(
        (trade * WEIGHTS.trade + area * WEIGHTS.area + trustVal * WEIGHTS.trust) / 100
      );
    }

    const reasons = [];
    if (trade >= 80) reasons.push('strong trade match');
    else if (trade > 0) reasons.push('partial trade match');
    else reasons.push('no trade match');
    if (area != null) {
      if (area >= 100) reasons.push('same area');
      else if (area >= 70) reasons.push('same state');
      else if (area > 0) reasons.push('nearby');
    }
    if (trust != null) reasons.push(`Trustack ${trust}`);

    return { vendor: v, score: composite, trade, area, trust, reasons };
  });

  return scored
    .filter((s) => s.trade > 0)
    .sort((a, b) => b.score - a.score || b.trade - a.trade)
    .slice(0, topN);
}

export default matchVendors;
